import React,{createContext,useCallback,useContext,useEffect,useMemo,useRef,useState}from"react";

const AuthContext=createContext(null);

const ROLE_KEY="group_services_session_role";

function getCookie(name){
  const cookies=document.cookie?document.cookie.split("; "):[];
  for(const cookie of cookies){
    const parts=cookie.split("=");
    const key=parts.shift();
    const value=parts.join("=");
    if(key===name)return decodeURIComponent(value);
  }
  return null;
}

export function AuthProvider({children}){
  const[user,setUser]=useState(null);
  const[loading,setLoading]=useState(true);
  const refreshPromiseRef=useRef(null);

  const setSessionRole=useCallback(role=>{
    if(role)sessionStorage.setItem(ROLE_KEY,role);
    else sessionStorage.removeItem(ROLE_KEY);
  },[]);

  const clearSession=useCallback(()=>{
    setUser(null);
    sessionStorage.removeItem(ROLE_KEY);
  },[]);

  const logout=useCallback(async()=>{
    const role=sessionStorage.getItem(ROLE_KEY)||user?.role||"";
    try{
      await fetch(`/api/login/logout/${role?`?role=${encodeURIComponent(role)}`:""}`,{
        method:"POST",
        headers:{"Content-Type":"application/json"},
        credentials:"include",
        body:JSON.stringify({role}),
      });
    }catch(error){
      console.error("Error cerrando sesión:",error);
    }finally{
      clearSession();
    }
  },[clearSession,user]);

  const refreshAccessToken=useCallback(async()=>{
    if(refreshPromiseRef.current)return refreshPromiseRef.current;

    refreshPromiseRef.current=(async()=>{
      try{
        const role=sessionStorage.getItem(ROLE_KEY);
        const url=role?`/api/login/refresh/?role=${encodeURIComponent(role)}`:"/api/login/refresh/";
        const response=await fetch(url,{
          method:"POST",
          credentials:"include",
        });

        if(!response.ok){
          if(response.status===401||response.status===403)clearSession();
          return null;
        }

        const data=await response.json();

        if(!data.user){
          clearSession();
          return null;
        }

        setUser(data.user);
        setSessionRole(data.user.role);
        return data.user;
      }catch(error){
        console.error("Error renovando sesión:",error);
        return null;
      }finally{
        refreshPromiseRef.current=null;
      }
    })();

    return refreshPromiseRef.current;
  },[clearSession,setSessionRole]);

  const login=useCallback(async(email,password)=>{
    const response=await fetch("/api/login/login/",{
      method:"POST",
      headers:{"Content-Type":"application/json"},
      credentials:"include",
      body:JSON.stringify({username:email,password}),
    });

    const data=await response.json();

    if(!response.ok)throw new Error(data.detail||"No se pudo iniciar sesión.");

    if(!data.user)throw new Error("No se pudo iniciar sesión.");

    setUser(data.user);
    setSessionRole(data.user.role);

    return data.user;
  },[setSessionRole]);

  const authFetch=useCallback(async(url,options={})=>{
    const makeRequest=()=>{
      const headers=new Headers(options.headers||{});
      const method=(options.method||"GET").toUpperCase();

      if(!["GET","HEAD","OPTIONS","TRACE"].includes(method)){
        const csrfToken=getCookie("csrftoken");
        if(csrfToken)headers.set("X-CSRFToken",csrfToken);
      }

      return fetch(url,{
        ...options,
        headers,
        credentials:"include",
      });
    };

    let response=await makeRequest();

    if(response.status!==401)return response;

    const refreshedUser=await refreshAccessToken();

    if(!refreshedUser)return response;

    return makeRequest();
  },[refreshAccessToken]);

  const loadSession=useCallback(async()=>{
    setLoading(true);

    try{
      await refreshAccessToken();
    }catch(error){
      console.error("Error cargando sesión:",error);
    }finally{
      setLoading(false);
    }
  },[refreshAccessToken]);

  useEffect(()=>{loadSession();},[loadSession]);

  const value=useMemo(()=>({
    user,
    loading,
    isAuthenticated:Boolean(user),
    login,
    logout,
    authFetch,
    refreshAccessToken,
  }),[user,loading,login,logout,authFetch,refreshAccessToken]);

  return(
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(){
  const context=useContext(AuthContext);
  if(!context)throw new Error("useAuth debe usarse dentro de AuthProvider.");
  return context;
}